/**
 * The account page: one card at a time, and every card a request to `api/`.
 *
 * The rules — what a valid password is, when a link expires, what an unconfirmed
 * account may do — are all in `worker/auth.js`. This file only decides which card
 * to show and says what the server said. It checks nothing the server does not
 * check again, so a message here is a courtesy and never a lock.
 *
 * LINKS FROM EMAILS ARRIVE IN THE FRAGMENT
 * `#verify=…`, `#reset=…` and `#change=…` (a new address), not `?verify=…`. A fragment is never sent to any
 * server, so the token is not in a request log or a `Referer`, and it is taken
 * out of the address bar the moment it has been read. And the page POSTs it
 * rather than the link doing the work: a mail scanner that opens every link in an
 * email to check it runs no script, so it cannot use up the link before the
 * person it was sent to has pressed it.
 */

import { mountNav } from '../nav.mjs';
// The note the masthead's Sign in button reads, so it can show who is signed in.
import { noteSignedIn, playerUrl } from '../account-link.js';
import { COUNTRIES, countryName } from '../countries.js';
import { lichessReturn, redirectUri, sitePath, startLichess, userLabel } from './lichess-oauth.js';

const API = './api/';

mountNav(document.getElementById('nav-slot'), { base: '../' });

const views = [...document.querySelectorAll('[data-view]')];
let lastEmail = '';

function show(name, { email } = {}) {
  if (email) lastEmail = email;
  for (const view of views) {
    const on = view.dataset.view === name;
    view.hidden = !on;
    if (!on) continue;
    view.querySelectorAll('[data-email]').forEach((el) => { el.textContent = lastEmail; });
    view.querySelectorAll('[data-error], [data-note]').forEach((el) => { el.hidden = true; });
    // The email carried from card to card, so nobody types it twice — but not
    // into the settings, where the address asked for is a NEW one, and where
    // nothing should take the focus from a card that has only just appeared.
    const outside = (input) => !input.closest('[data-settings]');
    view.querySelectorAll('input[type=email]').forEach((input) => { if (outside(input) && !input.value) input.value = lastEmail; });
    const empty = [...view.querySelectorAll('input')].find((input) => outside(input) && !input.value);
    if (empty) empty.focus();
  }
}

async function call(name, body) {
  const init = body === undefined
    ? { credentials: 'same-origin' }
    : {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  let response;
  try {
    response = await fetch(API + name, init);
  } catch {
    return { status: 0, data: { error: 'Could not reach the server. Check your connection.' } };
  }
  let data = {};
  try { data = await response.json(); } catch { /* an empty or broken body */ }
  if (!response.ok && !data.error) data.error = 'Something went wrong. Try again in a moment.';
  return { status: response.status, data };
}

function say(view, selector, text) {
  const el = view.querySelector(selector);
  if (!el) return;
  el.textContent = text;
  el.hidden = !text;
}

// The menu at the top right: who is signed in, the admin page for an admin, sign out.
const menu = document.querySelector('[data-account-menu]');

function showMenu(user) {
  menu.hidden = !user;
  menu.open = false;
  if (!user) return;
  const label = userLabel(user);
  menu.querySelector('[data-initial]').textContent = (label.match(/[a-z0-9]/i)?.[0] || '?').toUpperCase();
  menu.querySelector('[data-menu-email]').textContent = label;
  menu.querySelector('[data-admin]').hidden = !user.admin;
}

document.addEventListener('pointerdown', (event) => {
  if (menu.open && !menu.contains(event.target)) menu.open = false;
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !menu.open) return;
  menu.open = false;
  menu.querySelector('summary').focus();
});

/** The signed-in account as the server last described it. */
let current = null;

/**
 * Where to go once a username has been chosen, when signing in was on the way
 * somewhere else (Lichess's `back`). Empty for the ordinary case: the account card.
 */
let afterUsername = '';

/**
 * The username card: "Choose" the first time — asked of everybody who signs in
 * without one, before anything else — and "Change" after that.
 */
function askUsername(user) {
  const view = views.find((v) => v.dataset.view === 'username');
  const first = !user.username;
  view.querySelector('[data-username-title]').textContent = first ? 'Choose your username' : 'Change your username';
  const input = view.querySelector('input[name=username]');
  // A suggestion, never a choice made for them: the Lichess name if there is
  // one, or the front of the address, cut to what a username may be.
  const suggest = (user.lichess || String(user.email || '').split('@')[0] || '')
    .replace(/[^A-Za-z0-9_-]/g, '').replace(/^[^A-Za-z]+/, '').slice(0, 20);
  input.value = user.username || (suggest.length >= 3 ? suggest : '');
  view.querySelector('input[name=public]').checked = Boolean(user.public);
  show('username');
  input.select();
}

function signedIn(user, { note = '' } = {}) {
  current = user;
  noteSignedIn(true);
  showMenu(user);
  lastEmail = user.email || '';
  if (!user.username) { askUsername(user); return; }
  const view = views.find((v) => v.dataset.view === 'account');
  view.querySelector('[data-username]').textContent = user.username;
  view.querySelector('[data-public]').checked = user.public;
  view.querySelector('[data-public-say]').textContent = user.public
    ? 'On — you are on the leaderboard' : 'Off — only you see your games';
  view.querySelector('[data-created]').textContent = new Date(user.created)
    .toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  // An account made with Lichess alone has no address, and cannot drop its only way in.
  view.querySelectorAll('[data-email-row]').forEach((el) => { el.hidden = !user.email; });
  view.querySelector('[data-lichess]').textContent = user.lichess || 'Not linked';
  view.querySelector('[data-link-lichess]').hidden = Boolean(user.lichess);
  view.querySelector('[data-action=lichess-unlink]').hidden = !user.lichess || !user.email;
  // A Lichess-only account has no password to change or give, and no address.
  view.querySelectorAll('[data-needs-password]').forEach((el) => { el.hidden = !user.email; });
  fillAbout(user);
  show('account');
  say(view, '[data-note]', note);
}

/** Buttons that lock while their request is out, so a double press is one request. */
async function busy(button, work) {
  if (button.disabled) return;
  button.disabled = true;
  try { await work(); } finally { button.disabled = false; }
}

// --------------------------------------------------------------- the forms

const FORMS = {
  async login(values, view) {
    const { status, data } = await call('login', values);
    if (data.user) return signedIn(data.user);
    if (data.code === 'unverified') return show('unverified', { email: data.email });
    say(view, '[data-error]', data.error);
    if (status === 401) view.querySelector('input[name=password]').select();
  },
  async signup(values, view) {
    const { data } = await call('signup', values);
    if (data.ok) return show('check-inbox', { email: data.email });
    say(view, '[data-error]', data.error);
    if (data.field) view.querySelector(`input[name=${data.field}]`)?.focus();
  },
  async forgot(values, view) {
    const { data } = await call('forgot', values);
    if (data.ok) return show('reset-sent', { email: data.email });
    say(view, '[data-error]', data.error);
  },
  async profile(values, view) {
    const { data } = await call('profile', { username: String(values.username || '').trim(), public: values.public === '1' });
    if (!data.ok) {
      say(view, '[data-error]', data.error);
      view.querySelector('input[name=username]').focus();
      return;
    }
    const was = current?.username;
    const user = { ...current, username: data.username, public: data.public };
    if (afterUsername) { const next = afterUsername; afterUsername = ''; location.replace(next); return; }
    signedIn(user, { note: was && was !== data.username ? `You are ${data.username} now.` : '' });
  },
  async reset(values, view) {
    const { data } = await call('reset', { ...values, token: resetToken });
    if (data.user) return signedIn(data.user);
    if (data.code === 'bad-token') return show('bad-link');
    say(view, '[data-error]', data.error);
  },
};

for (const form of document.querySelectorAll('[data-form]')) {
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const view = form.closest('[data-view]');
    say(view, '[data-error]', '');
    const values = Object.fromEntries(new FormData(form));
    if (values.email !== undefined) lastEmail = String(values.email).trim();
    busy(form.querySelector('[type=submit]'), () => FORMS[form.dataset.form](values, view));
  });
}

// The public switch on the account card: saved the moment it changes.
document.querySelector('[data-public]').addEventListener('change', async (event) => {
  const box = event.currentTarget;
  const view = box.closest('[data-view]');
  box.disabled = true;
  const { data } = await call('profile', { public: box.checked });
  box.disabled = false;
  if (!data.ok) { box.checked = !box.checked; say(view, '[data-error]', data.error); return; }
  signedIn({ ...current, public: data.public }, {
    note: data.public ? 'You are on the leaderboard. People can watch and like your games.' : 'Your profile is private again.',
  });
});

// ------------------------------------------------------------- about you

const about = document.querySelector('[data-about-form]');
const countries = about.querySelector('select[name=country]');
// Every country, named in the reader's language and in their order.
countries.append(...COUNTRIES.map((code) => ({ code, name: countryName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(({ code, name }) => new Option(name, code)));

/** The bio and country as the server has them, fetched once a username is known. */
async function fillAbout(user) {
  about.querySelector('[data-my-page]').href = user.username ? playerUrl(user.username) : '../';
  if (!user.username) return;
  try {
    const response = await fetch(`../api/players/u/${encodeURIComponent(user.username)}`, { credentials: 'same-origin' });
    const { player } = await response.json();
    about.querySelector('textarea[name=bio]').value = player?.bio || '';
    countries.value = player?.country || '';
  } catch { /* offline: the fields stay empty, and saving still works */ }
}

about.addEventListener('submit', (event) => {
  event.preventDefault();
  sayIn(about, 'error', '');
  sayIn(about, 'note', '');
  const values = Object.fromEntries(new FormData(about));
  busy(about.querySelector('[type=submit]'), async () => {
    const { data } = await call('profile', { bio: values.bio, country: values.country });
    if (!data.ok) { sayIn(about, 'error', data.error); return; }
    about.querySelector('textarea[name=bio]').value = data.bio || '';
    countries.value = data.country || '';
    sayIn(about, 'note', 'Saved. It is on your page.');
  });
});

// ------------------------------------------------------- account settings

/** A message under one settings form, rather than on the card as a whole. */
function sayIn(block, kind, text) {
  const el = block.querySelector(kind === 'error' ? '[data-form-error]' : '[data-form-note]');
  if (!el) return;
  el.textContent = text;
  el.hidden = !text;
}

const SETTINGS = {
  async password(values, form) {
    const { data } = await call('password', values);
    if (!data.ok) {
      sayIn(form, 'error', data.error);
      form.querySelector(`input[name=${data.field || 'current'}]`)?.select();
      return;
    }
    form.reset();
    sayIn(form, 'note', 'Changed. Every other device has been signed out.');
  },
  async email(values, form) {
    const { data } = await call('email', values);
    if (!data.ok) {
      sayIn(form, 'error', data.error);
      form.querySelector(`input[name=${data.field || 'email'}]`)?.select();
      return;
    }
    form.reset();
    sayIn(form, 'note', `We sent a link to ${data.email}. Open it there to finish; until then your address stays as it is.`);
  },
  async delete(values, form) {
    const { data } = await call('delete', values);
    if (!data.ok) {
      sayIn(form, 'error', data.error);
      form.querySelector(`input[name=${data.field || 'confirm'}]`)?.select();
      return;
    }
    current = null;
    noteSignedIn(false);
    showMenu(null);
    show('closed');
  },
};

for (const form of document.querySelectorAll('[data-settings-form]')) {
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    sayIn(form, 'error', '');
    sayIn(form, 'note', '');
    const values = Object.fromEntries(new FormData(form));
    busy(form.querySelector('[type=submit]'), () => SETTINGS[form.dataset.settingsForm](values, form));
  });
}

/** The account's data, as a file: made in the page from the server's answer. */
async function download(button) {
  const block = button.closest('.setting-block');
  sayIn(block, 'error', '');
  const { data } = await call('export', {});
  if (data.error) { sayIn(block, 'error', data.error); return; }
  const name = `chesszz-${(current?.username || 'account').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.json`;
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ------------------------------------------------------------- the buttons

document.addEventListener('click', (event) => {
  const go = event.target.closest('[data-go]');
  if (go && go.dataset.go === 'username' && current) { askUsername(current); return; }
  if (go) { show(go.dataset.go); return; }
  const action = event.target.closest('[data-action]');
  if (!action) return;
  // The menu's Sign out is outside every card, so its errors go on the one showing.
  const view = action.closest('[data-view]') || views.find((v) => !v.hidden);

  if (action.dataset.action === 'resend') {
    busy(action, async () => {
      say(view, '[data-error]', '');
      say(view, '[data-note]', '');
      const { data } = await call('resend', { email: lastEmail });
      if (data.ok) say(view, '[data-note]', 'Sent. The previous link no longer works.');
      else say(view, '[data-error]', data.error);
    });
  }

  if (action.dataset.action === 'lichess') {
    busy(action, () => startLichess());
  }

  if (action.dataset.action === 'lichess-unlink') {
    busy(action, async () => {
      const { data } = await call('lichess-unlink', {});
      if (data.user) signedIn(data.user, { note: 'Lichess is no longer linked.' });
      else say(view, '[data-error]', data.error);
    });
  }

  if (action.dataset.action === 'signout-others') {
    const block = action.closest('.setting-block');
    busy(action, async () => {
      sayIn(block, 'error', '');
      const { data } = await call('signout-others', {});
      if (data.ok) sayIn(block, 'note', data.ended ? `Signed out on ${data.ended} other ${data.ended === 1 ? 'device' : 'devices'}.` : 'You were not signed in anywhere else.');
      else sayIn(block, 'error', data.error);
    });
  }

  if (action.dataset.action === 'export') {
    busy(action, () => download(action));
  }

  if (action.dataset.action === 'logout') {
    busy(action, async () => {
      const { data } = await call('logout', {});
      if (data.ok) { noteSignedIn(false); showMenu(null); show('signin'); }
      else say(view, '[data-error]', data.error);
    });
  }
});

// --------------------------------------------------------------- arriving

let resetToken = '';

/** Lichess has sent the person back: finish signing in, or say why not. */
async function finishLichess(back) {
  const fail = async (text) => {
    const { data } = await call('me');
    if (data.user) signedIn(data.user);
    else { noteSignedIn(false); show('signin'); }
    say(views.find((v) => !v.hidden), '[data-error]', text);
  };
  if (back.refused) return fail('Lichess sign-in was cancelled.');
  if (!back.ok) return fail('That Lichess sign-in did not start here. Try again.');
  show('lichess-wait');
  // The code, not a token: the server trades it with Lichess itself, so it only
  // ever trusts a sign-in this site started. See the `lichess` action in auth.js.
  const { data } = await call('lichess', { code: back.code, verifier: back.verifier, redirectUri: redirectUri() });
  if (!data.user) return fail(data.error || 'Lichess did not finish the sign-in. Try again.');
  // Only a path on this site: `back` came from a link anyone can make.
  const next = sitePath(back.back);
  // A first sign-in with Lichess is still a first sign-in: the username comes
  // before going back to wherever it started.
  if (next && !data.user.username) { afterUsername = next; signedIn(data.user); return; }
  if (next) { noteSignedIn(true); location.replace(next); return; }
  signedIn(data.user, { note: data.linked ? `Linked to ${data.user.lichess} on Lichess.` : '' });
}

async function start() {
  const back = lichessReturn();
  if (back) return finishLichess(back);

  const hash = new URLSearchParams(location.hash.slice(1));
  if (hash.has('verify') || hash.has('reset') || hash.has('lichess') || hash.has('change')) {
    // Out of the address bar and out of history before anything else happens.
    history.replaceState(null, '', location.pathname + location.search);
  }

  if (hash.has('verify')) {
    const { data } = await call('verify', { token: hash.get('verify') });
    if (data.user) return signedIn(data.user);
    return show(data.code === 'bad-token' ? 'bad-link' : 'signin');
  }

  if (hash.has('reset')) {
    resetToken = hash.get('reset');
    return show('reset');
  }

  // A link from the email sent to a NEW address: the account moves to it.
  if (hash.has('change')) {
    show('email-wait');
    const { data } = await call('email-confirm', { token: hash.get('change') });
    if (data.user) {
      noteSignedIn(true);
      return signedIn(data.user, { note: `Your address is ${data.email} now.` });
    }
    if (data.code === 'bad-token') return show('bad-link');
    const me = (await call('me')).data;
    if (me.user) signedIn(me.user); else show('signin');
    return say(views.find((v) => !v.hidden), '[data-error]', data.error);
  }

  // `#lichess=/path` from another page: sign in (or link) with Lichess, then go back there.
  if (hash.has('lichess')) {
    history.replaceState(null, '', location.pathname + location.search);
    show('lichess-wait');
    return startLichess({ back: hash.get('lichess') });
  }

  const { data } = await call('me');
  if (data.user) return signedIn(data.user);
  noteSignedIn(false);
  show(location.hash === '#signup' ? 'signup' : 'signin');
}

start();

// A link pasted into a tab that already has this page open changes only the
// fragment, which does not reload the page — so it would otherwise do nothing.
addEventListener('hashchange', () => {
  if (/^#(verify|reset|lichess|change)=/.test(location.hash)) start();
  else if (location.hash === '#signup') show('signup');
});
